import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ZuiBaseElement } from '@zywave/zui-base';
import { isPopoverSupported } from '@zywave/zui-base/dist/utils/support-popover.js';
import { style } from './zui-tooltip-css';
import '@zywave/zui-icons';

/**
 * Tooltips are unobtrusive messages that provide the user with simple information when hovering over a page element.
 *
 * @element zui-tooltip
 *
 * @slot trigger - Slot for the tooltip to anchor itself to. If this is not set, the `zui-help` icon will be used by default
 * @slot - Default, unnamed slot; for text to appear in the tooltip when the user hovers over the anchored element
 * @slot message - Deprecated: use the default, unnamed slot instead
 *
 * @cssprop [--zui-tooltip-margin] - Adjusts the margin of `<zui-tooltip>`
 * @cssprop [--zui-tooltip-trigger-color=var(--zui-blue)] - Fill color of the trigger icon
 */
export class ZuiTooltipElement extends ZuiBaseElement {
  /**
   * Sets which direction the tooltip should appear
   * @type { 'top' | 'right' | 'bottom' | 'left' }
   */
  @property({ type: String, reflect: true })
  position: ZuiTooltipPosition = 'right';

  @query('.trigger')
  _trigger: HTMLElement;

  @query('.tooltip')
  _tooltip: HTMLElement;

  #canHover: boolean | undefined = undefined;

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();

    this.#canHover = window.matchMedia('(hover: hover)').matches;
  }

  render() {
    return html`
      <div class="container">
        ${this.#renderTrigger()}
        <div id="tooltip" class="tooltip" popover role="tooltip">
          <slot></slot>
          <slot name="message"></slot>
        </div>
      </div>
    `;
  }

  #renderTrigger() {
    if (this.#canHover) {
      return html`<div
        class="trigger"
        @pointerenter="${this.#positionLogic}"
        @pointerover="${this.#showTooltip}"
        @pointerleave="${this.#hideTooltip}"
        popovertarget="tooltip"
      >
        <slot name="trigger">
          <zui-icon icon="zui-details" class="small"></zui-icon>
        </slot>
      </div>`;
    } else {
      return html`<div class="trigger" @click="${this.#toggleTooltip}" popovertarget="tooltip">
        <slot name="trigger">
          <zui-icon icon="zui-details" class="small"></zui-icon>
        </slot>
      </div>`;
    }
  }

  #toggleTooltip() {
    if (isPopoverSupported()) {
      this.#positionLogic();
      this._tooltip.togglePopover();
    }
  }

  #showTooltip() {
    if (isPopoverSupported()) {
      this._tooltip.showPopover();
    }
  }

  #hideTooltip() {
    if (isPopoverSupported()) {
      this._tooltip.hidePopover();
    }
  }

  // positioning logic madness
  async #positionLogic() {
    if (!isPopoverSupported()) {
      return;
    }

    await this.updateComplete;
    const triggerRect: DOMRect = this._trigger.getBoundingClientRect();
    const tooltipRect: DOMRect = this._tooltip.getBoundingClientRect();
    const tooltipArrowSize: number = 13; /* Arrow size 8px + 5px margin */

    if (triggerRect && tooltipRect) {
      const triggerTop = triggerRect.top;
      const triggerBottom = triggerRect.bottom;
      const triggerLeft = triggerRect.left;
      const triggerRight = triggerRect.right;
      const triggerHeight = triggerRect.height;
      const triggerWidth = triggerRect.width;

      // margins
      const tooltipComputedStyle = window.getComputedStyle(this._tooltip);
      const tooltipMarginTop = parseFloat(tooltipComputedStyle.marginTop);
      const tooltipMarginRight = parseFloat(tooltipComputedStyle.marginRight);
      const tooltipMarginBottom = parseFloat(tooltipComputedStyle.marginBottom);
      const tooltipMarginLeft = parseFloat(tooltipComputedStyle.marginLeft);

      const tooltipHeight = tooltipRect.height;
      const tooltipWidth = tooltipRect.width;

      // center align tooltip with trigger
      // and take margins into consideration
      const triggerVerticalCenter = () => {
        let math = triggerTop - tooltipHeight / 2 + triggerHeight / 2;

        if (tooltipMarginTop !== 0) {
          math += tooltipMarginTop;
        }

        if (tooltipMarginBottom !== 0) {
          math -= tooltipMarginBottom;
        }

        return math;
      };

      const triggerHorizontalCenter = () => {
        let math = triggerLeft - tooltipWidth / 2 + triggerWidth / 2;

        if (tooltipMarginLeft !== 0) {
          math += tooltipMarginLeft;
        }

        if (tooltipMarginRight !== 0) {
          math -= tooltipMarginRight;
        }

        return math;
      };

      // calculate tooltip positions based on which side of the trigger it will appear
      const tooltipTopPosition = triggerBottom - triggerHeight - tooltipHeight - tooltipMarginBottom - tooltipArrowSize;
      const tooltipBottomPosition = triggerTop + triggerHeight + tooltipArrowSize + tooltipMarginTop;
      const tooltipLeftPosition = triggerLeft - tooltipWidth - tooltipArrowSize - tooltipMarginRight;
      const tooltipRightPosition = triggerRight + tooltipArrowSize + tooltipMarginLeft;
      const tooltipCornerLeftPosition = triggerLeft - tooltipWidth - tooltipMarginRight;

      switch (this.position) {
        case 'top right':
        case 'right top':
          this._tooltip.style.top = `${tooltipTopPosition}px`;
          this._tooltip.style.left = `${triggerRight}px`;
          break;
        case 'top left':
        case 'left top':
          this._tooltip.style.top = `${tooltipTopPosition}px`;
          this._tooltip.style.left = `${tooltipCornerLeftPosition}px`;
          break;
        case 'top':
          this._tooltip.style.top = `${tooltipTopPosition}px`;
          this._tooltip.style.left = `${triggerHorizontalCenter()}px`;
          break;
        case 'bottom':
          this._tooltip.style.top = `${tooltipBottomPosition}px`;
          this._tooltip.style.left = `${triggerHorizontalCenter()}px`;
          break;
        case 'bottom left':
        case 'left bottom':
          this._tooltip.style.top = `${tooltipBottomPosition}px`;
          this._tooltip.style.left = `${tooltipCornerLeftPosition}px`;
          break;
        case 'bottom right':
        case 'right bottom':
          this._tooltip.style.top = `${tooltipBottomPosition}px`;
          this._tooltip.style.left = `${triggerRight}px`;
          break;
        case 'left':
          this._tooltip.style.top = `${triggerVerticalCenter()}px`;
          this._tooltip.style.left = `${tooltipLeftPosition}px`;
          break;
        case 'right':
        default:
          this._tooltip.style.top = `${triggerVerticalCenter()}px`;
          this._tooltip.style.left = `${tooltipRightPosition}px`;
          break;
      }

      this.#isTooltipOutsideViewport();
      this.requestUpdate();
    }
  }

  #isTooltipOutsideViewport() {
    const rect = this._tooltip.getBoundingClientRect();
    const vHeight = window.innerHeight;
    const vWidth = window.innerWidth;

    const isTopOutside = rect.top < 0;
    const isBottomOutside = rect.bottom > vHeight;
    const isLeftOutside = rect.left < 0;
    const isRightOutside = rect.right > vWidth;

    const sidesOutsideCount = [isTopOutside, isBottomOutside, isLeftOutside, isRightOutside].filter(
      (side) => side
    ).length;

    const isOneSideOutside = sidesOutsideCount === 1;
    const isTwoOrMoreSidesOutside = sidesOutsideCount >= 2;

    // this could be cleaner...
    if (isTwoOrMoreSidesOutside) {
      if (isTopOutside && isLeftOutside) {
        this.position = 'bottom right';

        if (isRightOutside) {
          this.position = 'bottom left';
        }
      }

      if (isTopOutside && isRightOutside) {
        this.position = 'bottom left';

        if (isLeftOutside) {
          this.position = 'bottom right';
        }
      }

      if (isBottomOutside && isLeftOutside) {
        this.position = 'top right';

        if (isRightOutside) {
          this.position = 'top left';
        }
      }

      if (isBottomOutside && isRightOutside) {
        this.position = 'top left';

        if (isLeftOutside) {
          this.position = 'top right';
        }
      }

      this.#positionLogic();
    }

    if (isOneSideOutside) {
      this.position = this.#getOppositePosition();
      this.#positionLogic();
    }
  }

  // when only one side is outside, flip the tooltip to the opposite side
  #getOppositePosition() {
    switch (this.position) {
      case 'top':
        return 'bottom';
      case 'bottom':
        return 'top';
      case 'left':
        return 'right';
      case 'right':
      default:
        return 'left';
    }
  }
}

window.customElements.define('zui-tooltip', ZuiTooltipElement);

export type ZuiTooltipPosition =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top right'
  | 'top left'
  | 'bottom right'
  | 'bottom left'
  | 'right top'
  | 'right bottom'
  | 'left top'
  | 'left bottom';

declare global {
  interface HTMLElementTagNameMap {
    'zui-tooltip': ZuiTooltipElement;
  }
}
